import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { TrendingDown, AlertTriangle } from 'lucide-react';

/** Milestone slip trend — reads /api/master-projects/health/slip-reasons
 *  and renders a small week-over-week bar chart with the top 3 causes.
 *  Available to Owner + DM Creative + DM Technical (backend-gated). */
export function MilestoneSlipTrend({ weeks = 8 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/master-projects/health/slip-reasons', { params: { weeks } })
      .then(r => { if (alive) { setData(r.data); setErr(null); } })
      .catch(e => { if (alive) setErr(e?.response?.status || 'err'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [weeks]);

  if (loading) {
    return <div className="card p-4 elev-1 text-[12px] text-[hsl(var(--foreground-muted))]">Loading slip trend…</div>;
  }
  if (err === 403) return null;   // role not permitted — hide silently
  if (err) {
    return <div className="card p-4 elev-1 text-[12px] text-[hsl(var(--foreground-muted))]">Slip trend unavailable.</div>;
  }
  if (!data || data.total === 0) {
    return (
      <div className="card p-4 elev-1" data-testid="milestone-slip-trend-empty">
        <div className="flex items-center gap-2 mb-1">
          <TrendingDown size={13} className="text-[hsl(var(--foreground-muted))]" />
          <span className="text-[13px] font-semibold">Milestone slip trend</span>
        </div>
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          No milestone shifts recorded in the last {weeks} weeks. That&rsquo;s a healthy streak.
        </div>
      </div>
    );
  }

  const maxCount = Math.max(1, ...data.weeks.map(w => w.total || 0));

  return (
    <div className="card p-4 elev-1" data-testid="milestone-slip-trend">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingDown size={13} className="text-[hsl(var(--warning))]" />
          <span className="text-[13px] font-semibold">Milestone slip trend</span>
          <span className="text-[11px] text-[hsl(var(--foreground-muted))]">last {weeks} weeks</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <AlertTriangle size={11} className="text-[hsl(var(--warning))]" />
          <span className="text-[hsl(var(--foreground-muted))]">Total shifts</span>
          <span className="font-semibold tabular-nums">{data.total}</span>
        </div>
      </div>

      {/* Weekly bars */}
      <div className="flex items-end gap-1 h-[64px]" data-testid="slip-trend-bars">
        {data.weeks.map(w => {
          const h = w.total ? Math.max(6, (w.total / maxCount) * 100) : 4;
          const isHot = w.total >= Math.ceil(maxCount * 0.66);
          return (
            <div key={w.week} className="flex-1 flex flex-col items-center justify-end min-w-0" title={`${w.week_start}: ${w.total} shifts`}>
              <div className="w-full rounded-sm transition-all"
                   style={{
                     height: `${h}%`,
                     background: w.total === 0
                       ? 'hsl(var(--surface-2))'
                       : isHot
                         ? 'hsl(var(--danger))'
                         : 'hsl(var(--warning))',
                   }}
                   data-testid={`slip-bar-${w.week}`} />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-[hsl(var(--foreground-subtle))]">
        <span>{data.weeks[0]?.week_start}</span>
        <span>{data.weeks[data.weeks.length - 1]?.week_start}</span>
      </div>

      {/* Top 3 causes */}
      {data.overall_top?.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-1.5">
            Top slip causes
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {data.overall_top.map(t => (
              <span key={t.reason} className="chip chip-warning" data-testid={`slip-cause-${t.reason}`}>
                {t.label} <span className="opacity-70">·</span> <span className="tabular-nums font-mono">{t.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
